import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Lock, Shield, Database, Eye, UserCheck, Globe } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-10 h-10 text-blue-400" />
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground">
                  Last updated: February 2026
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                1. Introduction
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  SportsAnalysisAB ("we", "us", or "our") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you use our website sportsanalysisab.ca (the "Service").
                </p>
                <p>
                  This policy complies with Canada's <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) 
                  and Alberta's <em>Personal Information Protection Act</em> (PIPA).
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="bg-card border border-border rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                2. Information We Collect
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p><strong className="text-foreground">Information You Provide:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Account registration information (name, email address)</li>
                  <li>Age verification confirmation</li>
                  <li>Payment information (processed securely through Stripe)</li>
                  <li>User preferences (favorite teams, notification settings)</li>
                  <li>Prediction history and activity</li>
                </ul>
                
                <p className="mt-4"><strong className="text-foreground">Information Collected Automatically:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Device information (browser type, operating system)</li>
                  <li>IP address and approximate location</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-400" />
                3. How We Use Your Information
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>We use your information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Process transactions and manage subscriptions</li>
                  <li>Personalize your experience and deliver relevant content</li>
                  <li>Communicate with you about updates, offers, and support</li>
                  <li>Analyze usage patterns to enhance our platform</li>
                  <li>Comply with legal obligations</li>
                  <li>Enforce our Terms of Service and protect against fraud</li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>We do NOT sell your personal information. We may share information with:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Service Providers:</strong> Third parties who assist in operating our Service (e.g., Stripe for payments, Resend for emails)</li>
                  <li><strong className="text-foreground">Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong className="text-foreground">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong className="text-foreground">With Your Consent:</strong> When you explicitly authorize us to share</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section className="bg-green-500/10 border border-green-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-green-400 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                5. Data Security
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We implement industry-standard security measures to protect your personal information:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                  <li>Secure password hashing (bcrypt)</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Secure payment processing through PCI-compliant providers</li>
                </ul>
                <p className="mt-4">
                  While we strive to protect your information, no method of electronic transmission 
                  or storage is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-purple-400" />
                6. Your Privacy Rights (Under PIPA and PIPEDA)
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>As a resident of Canada/Alberta, you have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Access:</strong> Request a copy of personal information we hold about you</li>
                  <li><strong className="text-foreground">Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                  <li><strong className="text-foreground">Withdrawal of Consent:</strong> Withdraw consent for data processing at any time</li>
                  <li><strong className="text-foreground">Complaint:</strong> Lodge a complaint with the Office of the Information and Privacy Commissioner of Alberta (OIPC)</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, contact us at Contact Us page. 
                  We will respond within 30 days as required by law.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>We use cookies and similar technologies to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Essential Cookies:</strong> Enable core functionality (authentication, security)</li>
                  <li><strong className="text-foreground">Analytics Cookies:</strong> Understand how you use our Service</li>
                  <li><strong className="text-foreground">Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="mt-4">
                  You can control cookies through your browser settings. Disabling certain cookies 
                  may affect the functionality of our Service.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                8. Data Retention
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We retain your personal information for as long as necessary to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide our Service and maintain your account</li>
                  <li>Comply with legal, accounting, or reporting requirements</li>
                  <li>Resolve disputes and enforce our agreements</li>
                </ul>
                <p className="mt-4">
                  When you delete your account, we will delete or anonymize your personal information 
                  within 90 days, except where retention is required by law.
                </p>
              </div>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                9. International Data Transfers
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Your information may be transferred to and processed in countries outside Canada 
                  (e.g., where our service providers operate). We ensure appropriate safeguards 
                  are in place in accordance with PIPEDA requirements for cross-border transfers.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section className="bg-red-500/10 border border-red-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-red-400 mb-4">
                10. Children's Privacy
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Our Service is intended for users who are 18 years of age or older. 
                  We do not knowingly collect personal information from children under 18. 
                  If we become aware that we have collected information from a minor, 
                  we will take steps to delete that information immediately.
                </p>
              </div>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of 
                  material changes by posting the new Privacy Policy on this page and updating 
                  the "Last updated" date. We encourage you to review this policy periodically.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                12. Contact Us
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  For questions or concerns about this Privacy Policy or our data practices, contact:
                </p>
                <p>
                  <strong className="text-foreground">Privacy Officer</strong><br />
                  SportsAnalysisAB<br />
                  Email: Contact Us page<br />
                  Province of Alberta, Canada
                </p>
                <p className="mt-4">
                  You may also contact the Office of the Information and Privacy Commissioner of Alberta:<br />
                  Website: <a href="https://www.oipc.ab.ca" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">www.oipc.ab.ca</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
